import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import { useEditorStore } from '../store/editor'
import type { ExportPreset } from '../api/types'

// 导出弹窗：纯浏览器渲染（clientExport：SVG 场景 WebCodecs/MediaRecorder，map-trajectory ECharts 离屏）。
// 失败直接报错（不降级服务端）—— 最大化减服务器负载。
// 注意：map-trajectory 桌面 Chrome 无 mp4 录制能力时会报错（用户要的：不回退服务端）。
export default function ExportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const sceneId = useEditorStore((s) => s.sceneId)
  const data = useEditorStore((s) => s.data)
  const duration = useEditorStore((s) => s.duration)

  // 水印由后台配置（/exports/config.watermark），用户不可改；null=未加载或后台未配
  const [watermark, setWatermark] = useState<{ text: string; position: string; opacity: number; enabled: boolean } | null>(null)
  const [presets, setPresets] = useState<Record<string, ExportPreset>>({})
  const [preset, setPreset] = useState('universal')
  const [state, setState] = useState<'idle' | 'rendering' | 'done' | 'error'>('idle')
  const [error, setError] = useState('')
  const [progress, setProgress] = useState<{ pct: number; msg: string } | null>(null)

  useEffect(() => {
    let alive = true
    api
      .getExportConfig()
      .then((c) => {
        if (alive) {
          setWatermark(c.watermark ?? null)
        }
      })
    api
      .getExportPresets()
      .then((p) => {
        if (alive) setPresets(p.presets)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const doRender = async () => {
    if (!sceneId) return
    setState('rendering')
    setError('')
    setProgress({ pct: 0, msg: '' })
    try {
      const { exportVideo } = await import('../export/clientExport')
      const { blob } = await exportVideo({
        sceneId,
        data,
        duration,
        watermark: watermark?.enabled
          ? { text: watermark.text, position: watermark.position, opacity: watermark.opacity }
          : undefined,
        preset: presets[preset],
        onProgress: (pct, msg) => setProgress({ pct, msg }),
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `visword-${sceneId}.mp4`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setState('done')
    } catch (e: unknown) {
      setState('error')
      setError(e instanceof Error ? e.message : t('export.failed'))
    } finally {
      setProgress(null)
    }
  }

  const busy = state === 'rendering'

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-export" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('export.title')}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="close">
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="ex-field">
            <label>{t('export.platform')}</label>
            <select className="select" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {Object.keys(presets).map((k) => (
                <option key={k} value={k}>
                  {k} · {presets[k].resolution.width}×{presets[k].resolution.height}
                </option>
              ))}
            </select>
          </div>
          {progress && (
            <div className="ex-progress">
              <div className="ex-progress-fill" style={{ width: `${progress.pct}%` }} />
              <span className="ex-progress-text">
                {progress.pct}% · {progress.msg}
              </span>
            </div>
          )}

          {state === 'error' && <div className="error-box">{error}</div>}

          <button
            className="btn btn-primary btn-lg"
            style={{ width: '100%', marginTop: 4 }}
            disabled={busy}
            onClick={doRender}
          >
            {busy ? `◐ ${t('export.rendering')}` : state === 'done' ? `✓ ${t('export.done')}` : t('export.render')}
          </button>
        </div>
      </div>
    </div>
  )
}
